import React, { Component } from 'react';
import { actions, Control, Errors } from 'react-redux-form/immutable';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import classnames from 'classnames';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  ListGroup,
  ListGroupItem,
} from 'reactstrap';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { throttle } from 'throttle-debounce';
import { sortArrayCaseInsensitive } from '../../util/collectionUtils';
import { isA11yClick } from '../../util/accessabilityUtils';
import { IconWithTooltip } from '../icon/IconWithTooltip';
import { FieldHelpIcon } from '../icon/FieldHelpIcon';
import { helpModalControl } from '../modals/HelpModalControl';

const MIN_DROPDOWN_HEIGHT = 150;
const DROPDOWN_BOTTOM_PADDING = 10;

class LabeledSelectUnwrapped extends Component {
  constructor(props) {
    super(props);
    this.id = `i${uuid()}`;
    this.toggle = this.toggle.bind(this);
    this.getOptions = this.getOptions.bind(this);
    this.select = this.select.bind(this);
    this.onResize = throttle(250, this.onResize, false).bind(this);
    this.afterToggle = this.afterToggle.bind(this);
    this.selectValue = this.selectValue.bind(this);
    this.renderFilterRow = this.renderFilterRow.bind(this);
    this.doAdd = this.doAdd.bind(this);
    this.renderDropdownToggle = this.renderDropdownToggle.bind(this);
    this.splitSelected = this.splitSelected.bind(this);
    this.removeListItem = this.removeListItem.bind(this);
    this.sortOptions = this.sortOptions.bind(this);
    this.addOption = this.addOption.bind(this);
    this.resolveValue = this.resolveValue.bind(this);
    this.handleHelpClick = this.handleHelpClick.bind(this);
    this.state = {
      dropdownOpen: false,
      maxHeight: 0,
      value: '',
      options: [],
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.onResize);
    window.addEventListener('scroll', this.onResize);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onResize);
    window.removeEventListener('resize', this.onResize);
  }

  onResize() {
    const { maxHeight, dropdownOpen } = this.state;

    if (dropdownOpen) {
      // eslint-disable-next-line prefer-destructuring
      let windowTop = window.scrollY;
      if (windowTop == null) {
        windowTop = window.pageYOffset;
      }
      const windowHeight = window.innerHeight;
      const windowBottom = windowTop + windowHeight;

      const { top: relativeDropdownTop } = this.dropdownRef
        .getElementsByClassName('haz-custom-select-dropdown')[0].getBoundingClientRect();
      const dropdownTop = windowTop + relativeDropdownTop;

      const maxDropdownBottom = windowBottom - DROPDOWN_BOTTOM_PADDING;

      const newMaxHeight = Math.max(maxDropdownBottom - dropdownTop, MIN_DROPDOWN_HEIGHT);
      if (newMaxHeight !== maxHeight) {
        this.setState({ maxHeight: newMaxHeight });
      }
    }
  }

  handleHelpClick(e) {
    e.preventDefault();
    const { toggleHelpModal, helpModalId } = this.props;
    if (helpModalId) {
      toggleHelpModal(helpModalId);
    }
  }

  splitSelected() {
    const {
      selected, splitString, multiselect,
    } = this.props;
    if (multiselect) {
      return selected.split(splitString).map(s => s.trim());
    }
    return [selected];
  }

  sortOptions() {
    const {
      childOptions, sorted, requiresValue, multiselect,
    } = this.props;
    const { options: customOptions } = this.state;
    const options = [...customOptions];
    let defaultOption = null;
    childOptions.forEach((o) => {
      if (`${o.id}` === '') {
        defaultOption = o;
      } else {
        options.push(o);
      }
    });

    if (!requiresValue && !defaultOption) {
      /* defaultOption = { id: '', description: '—' }; */
      defaultOption = { id: '', description: 'No Selection' };
    }

    if (sorted) {
      sortArrayCaseInsensitive(options, o => o.description);
    }

    if (!requiresValue && defaultOption && !multiselect) {
      options.unshift(defaultOption);
    }

    return options;
  }

  resolveValue(descriptor) {
    const { prependId } = this.props;
    return (prependId && `${descriptor.id}` !== '') ? `${descriptor.id} - ${descriptor.description}` : descriptor.description;
  }

  getOptions() {
    const { filterable, multiselect } = this.props;
    const { value } = this.state;
    const options = this.sortOptions();

    const ids = this.splitSelected();
    const selectedOptions = options.filter(o => ids.indexOf(`${o.id}`) !== -1);

    if (!multiselect && !selectedOptions.length && options.length) {
      selectedOptions.push(options[0]);
    }

    const filteredOptions = options
      .filter(o => !filterable || !value || `${o.id}` === '' || o.description.toUpperCase().indexOf(value.toUpperCase()) !== -1)
      .map(o => (
        <DropdownItem key={o.id} active={ids.indexOf(`${o.id}`) !== -1} onClick={e => this.select(e, o.id)} onKeyDown={e => this.select(e, o.id)}>
          {this.resolveValue(o)}
        </DropdownItem>
      ));

    return {
      selectedOptions,
      options: filteredOptions,
    };
  }

  select(event, key) {
    const {
      change, parentModel, fieldId, multiselect, selected, splitString,
    } = this.props;
    if (isA11yClick(event)) {
      const newSelected = `${key}`;
      if (multiselect && selected != null && selected.length) {
        const ids = this.splitSelected();
        if (ids.indexOf(newSelected) === -1) {
          change(`${parentModel}.${fieldId}`, `${selected}${splitString}${newSelected}`);
        }
      } else {
        change(`${parentModel}.${fieldId}`, newSelected);
      }
    }
  }

  selectValue(val) {
    const { change, parentModel, fieldId } = this.props;
    change(`${parentModel}.${fieldId}`, val);
  }

  afterToggle() {
    this.onResize();
  }

  toggle() {
    this.setState(
      prevState => ({ dropdownOpen: !prevState.dropdownOpen, value: '' }),
      this.afterToggle,
    );
  }

  addOption(o) {
    this.setState(({ options }) => ({ options: [...options, o] }));
  }

  doAdd() {
    const { handleAdd } = this.props;
    const { value } = this.state;
    const toAdd = handleAdd(value);
    if (toAdd === true) {
      this.addOption({ id: value, description: value });
      this.selectValue(value);
      this.toggle();
    } else if (toAdd) {
      this.addOption(toAdd);
      this.selectValue(toAdd.id);
      this.toggle();
    } else {
      this.toggle();
    }
  }

  renderFilterRow() {
    const { filterable, addable } = this.props;

    if (!filterable && !addable) {
      return null;
    }

    let message = '';
    if (filterable && addable) {
      message = 'Filter / Add';
    } else if (filterable) {
      message = 'Filter';
    } else {
      message = 'Add';
    }

    return (
      <div className="row pr-3 pl-3 pt-1 pb-1">
        <div className="col-sm-auto align-middle"><span className="align-middle">{message}</span></div>
        <div className="col-sm">
          <div className="row">
            <div className="col">
              <input
                type="text"
                className="form-control"
                name="_custom_select_option_search"
                onKeyDown={(e) => {
                  const { keyCode } = e;
                  if (keyCode === 13) {
                    e.preventDefault();
                  }
                }}
                onChange={(event) => {
                  this.setState({ value: event.target.value });
                }}
              />
            </div>
            {addable && (
            <div className="col-auto">
              <IconWithTooltip
                handleClick={this.doAdd}
                ariaLabel="add"
                tooltip="Add Item"
                icon="plus-circle"
                className="haz-add-option-button mr-3"
                size="lg"
              />
            </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  removeListItem(id) {
    const {
      splitString, change, parentModel, fieldId,
    } = this.props;
    const toRemove = `${id}`;
    const ids = this.splitSelected().filter(r => r !== toRemove);
    let newSelected = '';
    let first = true;
    if (ids.length > 0) {
      ids.forEach((s) => {
        if (first) {
          newSelected = s;
          first = false;
        } else {
          newSelected = `${newSelected}${splitString}${s}`;
        }
      });
    }
    change(`${parentModel}.${fieldId}`, newSelected);
  }

  renderDropdownToggle(selectedOptions) {
    const {
      readOnly, valid, multiselect, label, fieldId,
    } = this.props;

    if (multiselect) {
      return (
        <div>
          <ListGroup>
            {selectedOptions.map(({ id, description }) => (
              <ListGroupItem key={id}>
                <div className="row">
                  <div className="col-auto">
                    <IconWithTooltip
                      handleClick={() => this.removeListItem(id)}
                      ariaLabel="remove"
                      tooltip="Remove Item"
                      icon="minus-circle"
                      className="haz-add-option-button mr-3"
                      size="lg"
                    />
                  </div>
                  <div className="col">{this.resolveValue({ id, description })}</div>
                </div>

              </ListGroupItem>
            ))}
          </ListGroup>
          <DropdownToggle disabled={readOnly}>Add</DropdownToggle>
        </div>
      );
    }

    return (
      <DropdownToggle
        tag="button"
        type="button"
        className={classnames({ 'custom-select': true, 'is-invalid': !valid })}
        aria-label={`${this.resolveValue(selectedOptions[0])} ${label}`}
        // title={`${fieldId.replace(/([A-Z])/g, ' $1').toLowerCase()}`}
        title={fieldId}
        aria-hidden={readOnly}
        aria-disabled={readOnly}
        disabled={readOnly}
      >
        {selectedOptions.length && this.resolveValue(selectedOptions[0])}
      </DropdownToggle>
    );
  }

  render() {
    const {
      label, readOnly, fieldId, formGroupClassName, helpModalId, valid,
    } = this.props;
    const {
      dropdownOpen,
      maxHeight,
    } = this.state;

    const model = `.${fieldId}`;
    const opts = this.getOptions();
    const { selectedOptions, options } = opts;
    // console.log(model);

    return (
      <div
        className={classnames(['form-group', 'haz-custom-select', ...formGroupClassName])}
        ref={(ref) => { this.dropdownRef = ref; }}
      >
        <label htmlFor={this.id}>
          <div className="haz-help-icon-inline">
            <span>{label}</span>
            {helpModalId && <span><FieldHelpIcon tooltipLabel={helpModalId} handleClick={this.handleHelpClick} /></span>}
          </div>
        </label>

        <div style={{ display: 'none' }}>
          {/* eslint-disable-next-line react/jsx-pascal-case */}
          <Control.text
            model={model}
            id={this.id}
            mapProps={{
              readOnly,
              tabIndex: '0',
              className: ({ fieldValue }) => classnames({ 'form-control': true, 'is-invalid': !fieldValue.valid }),
            }}
          />
        </div>

        {/* TODO: need to pass aria-hidden="true" and aria-disabled="true" to the button specifically */}
        <Dropdown className={classnames({ 'is-invalid': !valid })} isOpen={dropdownOpen} toggle={this.toggle}>
          {this.renderDropdownToggle(selectedOptions)}
          <DropdownMenu
            flip={false}
            className="haz-custom-select-dropdown"
            style={{ maxHeight }}
          >
            {this.renderFilterRow()}
            {options}
          </DropdownMenu>
        </Dropdown>
        <Errors
          model={model}
          component="div"
          wrapper={({ children: childs }) => <div className="invalid-feedback">{childs}</div>}
        />
      </div>
    );
  }
}


LabeledSelectUnwrapped.propTypes = {
  filterable: PropTypes.bool,
  addable: PropTypes.bool,
  sorted: PropTypes.bool,
  requiresValue: PropTypes.bool,
  parentModel: PropTypes.string.isRequired,
  fieldId: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  childOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  })).isRequired,
  readOnly: PropTypes.bool,
  selected: PropTypes.string.isRequired,
  change: PropTypes.func.isRequired,
  valid: PropTypes.bool.isRequired,
  handleAdd: PropTypes.func,
  multiselect: PropTypes.bool,
  splitString: PropTypes.string,
  formGroupClassName: PropTypes.arrayOf(PropTypes.string),
  prependId: PropTypes.bool,
  toggleHelpModal: PropTypes.func.isRequired,
  helpModalId: PropTypes.string,
};

LabeledSelectUnwrapped.defaultProps = {
  readOnly: false,
  sorted: true,
  requiresValue: false,
  filterable: false,
  addable: false,
  handleAdd: () => true,
  multiselect: false,
  splitString: ',',
  formGroupClassName: [],
  prependId: false,
  helpModalId: '',
};

const mapStateToProps = (state, { parentModel, fieldId }) => ({
  selected: state.getIn([parentModel, fieldId]) ? state.getIn([parentModel, fieldId]) : '',
  valid: state.get('forms')[parentModel][fieldId].valid,
});

const mapDispatchToProps = dispatch => bindActionCreators({
  change: actions.change,
}, dispatch);


export const LabeledSelect = helpModalControl(connect(mapStateToProps, mapDispatchToProps)(LabeledSelectUnwrapped));
